import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * CalendarioReserva — calendario mensual interactivo para que el usuario
 * elija un día con slots disponibles.
 *
 * Props:
 *  - slots: Array<{ fecha:'YYYY-MM-DD', hora_inicio, hora_fin, tecnico_id }>
 *  - fechaInicio, fechaLimite: 'YYYY-MM-DD'
 *  - fechaSeleccionada: 'YYYY-MM-DD' | null
 *  - onSeleccionarFecha: (fecha) => void
 */
export default function CalendarioReserva({
  slots,
  fechaInicio,
  fechaLimite,
  fechaSeleccionada,
  onSeleccionarFecha,
}) {
  const primerMes = useMemo(() => {
    if (!fechaInicio) return null;
    const [y, m] = fechaInicio.split('-').map(Number);
    return { year: y, month: m };
  }, [fechaInicio]);

  const ultimoMes = useMemo(() => {
    if (!fechaLimite) return null;
    const [y, m] = fechaLimite.split('-').map(Number);
    return { year: y, month: m };
  }, [fechaLimite]);

  const [mesActual, setMesActual] = useState(primerMes);

  React.useEffect(() => {
    if (!fechaSeleccionada || !mesActual) return;
    const [y, m] = fechaSeleccionada.split('-').map(Number);
    if (y !== mesActual.year || m !== mesActual.month) {
      setMesActual({ year: y, month: m });
    }
    // eslint-disable-next-line
  }, [fechaSeleccionada]);

  const slotsPorFecha = useMemo(() => {
    const m = new Map();
    for (const s of slots) m.set(s.fecha, (m.get(s.fecha) || 0) + 1);
    return m;
  }, [slots]);

  if (!primerMes || !ultimoMes || !mesActual) {
    return <div className="text-sm text-slate-400">Sin rango disponible.</div>;
  }

  const puedeIrAtras =
    mesActual.year > primerMes.year ||
    (mesActual.year === primerMes.year && mesActual.month > primerMes.month);
  const puedeIrAdelante =
    mesActual.year < ultimoMes.year ||
    (mesActual.year === ultimoMes.year && mesActual.month < ultimoMes.month);

  function navega(delta) {
    let m = mesActual.month + delta;
    let y = mesActual.year;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    setMesActual({ year: y, month: m });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => navega(-1)}
          disabled={!puedeIrAtras}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mes anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="font-medium text-slate-800 text-sm">
          {nombreMes(mesActual.month)} {mesActual.year}
        </div>
        <button
          type="button"
          onClick={() => navega(1)}
          disabled={!puedeIrAdelante}
          className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Mes siguiente"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <Grid
        year={mesActual.year}
        month={mesActual.month}
        fechaInicio={fechaInicio}
        fechaLimite={fechaLimite}
        slotsPorFecha={slotsPorFecha}
        fechaSeleccionada={fechaSeleccionada}
        onSeleccionarFecha={onSeleccionarFecha}
      />

      <Leyenda />
    </div>
  );
}

function Grid({
  year, month, fechaInicio, fechaLimite,
  slotsPorFecha, fechaSeleccionada, onSeleccionarFecha,
}) {
  const primerDia = new Date(Date.UTC(year, month - 1, 1));
  const totalDias = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const primerDow = (primerDia.getUTCDay() + 6) % 7;

  const celdas = [];
  for (let i = 0; i < primerDow; i++) celdas.push(null);
  for (let d = 1; d <= totalDias; d++) celdas.push(d);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-7 bg-slate-50 text-[10px] text-slate-500 uppercase tracking-wide">
        {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center font-medium">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-100">
        {celdas.map((dia, idx) => {
          if (dia === null) {
            return <div key={idx} className="bg-white aspect-square" />;
          }
          const fecha = `${year}-${String(month).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          const fuera = fecha < fechaInicio || fecha > fechaLimite;
          const slots = slotsPorFecha.get(fecha) || 0;
          const seleccionado = fechaSeleccionada === fecha;
          const clickable = !fuera && slots > 0;

          let clase = 'bg-slate-50 text-slate-300';
          if (seleccionado) {
            clase = 'bg-emerald-600 text-white hover:bg-emerald-700';
          } else if (!fuera && slots > 0) {
            clase = 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100';
          }

          return (
            <button
              key={fecha}
              type="button"
              disabled={!clickable}
              onClick={() => onSeleccionarFecha(fecha)}
              className={
                'aspect-square text-xs flex flex-col items-center justify-center transition-colors ' +
                clase +
                (clickable ? ' cursor-pointer' : ' cursor-default')
              }
              title={clickable ? `${slots} slot${slots !== 1 ? 's' : ''} disponible${slots !== 1 ? 's' : ''}` : ''}
            >
              <span className="font-medium">{dia}</span>
              {!fuera && slots > 0 && (
                <span className={'text-[9px] mt-0.5 ' + (seleccionado ? 'text-emerald-100' : 'text-emerald-600')}>
                  {slots}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
function nombreMes(m) { return MESES[m - 1] || ''; }

function Leyenda() {
  return (
    <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 mt-2">
      <Item clase="bg-emerald-50 text-emerald-700" label="Con slots disponibles" />
      <Item clase="bg-emerald-600 text-white" label="Día seleccionado" />
      <Item clase="bg-slate-50 text-slate-300" label="Sin slots / fuera de rango" />
    </div>
  );
}
function Item({ clase, label }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={`inline-block w-4 h-4 rounded ${clase}`} />
      <span>{label}</span>
    </div>
  );
}
